// MfaSetupForm.jsx
import { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import apiClient from "api/apiClient";
import useMessageToast from "hooks/useMessageToast";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { setAuth } from "state/slices/authSlice"; // { isAuthenticated, user }

const API = process.env.REACT_APP_BASE_URL; // https://api.greenleaves.app

const MfaSetupForm = () => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);

  const [mfaEnabled, setMfaEnabled] = useState(!!user?.mfaEnabled);
  const [qrCode, setQrCode] = useState("");
  const [secret, setSecret] = useState("");
  const [token, setToken] = useState("");
  const [status, setStatus] = useState("");
  useMessageToast(status, "info");
  const [loading, setLoading] = useState(false);
  const [backupCodes, setBackupCodes] = useState([]);

  const syncUserState = (updatedUser) => {
    dispatch(
      setAuth({
        isAuthenticated: true,
        user: { ...(user || {}), ...updatedUser },
      }),
    );
  };

  const copyBackupCodes = async () => {
    if (!backupCodes.length || !navigator?.clipboard?.writeText) {
      return;
    }

    try {
      await navigator.clipboard.writeText(backupCodes.join("\n"));
      setStatus("Backup codes copied. Store them somewhere safe.");
    } catch {
      setStatus("Could not copy backup codes automatically. Please copy them manually.");
    }
  };

  const handleEnableMfa = async () => {
    setLoading(true);
    setStatus("");
    try {
      // cookie-based, CSRF handled by interceptor
      const { data } = await apiClient.get(`${API}/user/setup-mfa`, {
        withCredentials: true,
      });
      setQrCode(data.qrCodeImageUrl);
      setSecret(data.secret);
      setBackupCodes([]);
    } catch (e) {
      setStatus("Failed to load MFA setup.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyMfa = async () => {
    setLoading(true);
    setStatus("");
    try {
      // Verify and enable (server marks user.mfaEnabled = true)
      const { data } = await apiClient.post(
        `${API}/user/verify-mfa`,
        { secret, token },
        { withCredentials: true },
      );

      // If server returns the updated user, prefer it; otherwise fetch /user/me
      let updatedUser = data?.user;
      if (!updatedUser) {
        const me = await apiClient.get(`${API}/user/me`, {
          withCredentials: true,
        });
        updatedUser = me.data?.user || me.data;
      }

      // Flip local UI
      setMfaEnabled(true);
      setQrCode("");
      setToken("");
      setBackupCodes(Array.isArray(data?.backupCodes) ? data.backupCodes : []);
      setStatus("MFA enabled successfully. Save your backup codes now.");
      syncUserState({ ...(updatedUser || {}), mfaEnabled: true });
    } catch (e) {
      setStatus("❌ Invalid code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerateBackupCodes = async () => {
    setLoading(true);
    setStatus("");
    try {
      const { data } = await apiClient.post(
        `${API}/user/mfa-backup-codes/regenerate`,
        {},
        { withCredentials: true },
      );

      setBackupCodes(Array.isArray(data?.backupCodes) ? data.backupCodes : []);
      setStatus("New backup codes generated. Your previous backup codes no longer work.");

      if (data?.user) {
        syncUserState(data.user);
      }
    } catch (e) {
      setStatus("Failed to regenerate backup codes.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="settings-section">
      <h3>Multi-Factor Authentication</h3>
      <p>Secure your account with TOTP-based two-factor authentication.</p>

      {mfaEnabled ? (
        <Stack spacing={2}>
          <div className="mfa-status enabled">✅ MFA is currently enabled.</div>
          <Box>
            <Button
              variant="contained"
              onClick={handleRegenerateBackupCodes}
              disabled={loading}
            >
              {loading ? <CircularProgress size={20} color="inherit" /> : "Generate New Backup Codes"}
            </Button>
          </Box>
        </Stack>
      ) : qrCode ? (
        <div className="mfa-setup">
          <img src={qrCode} alt="MFA QR Code" />
          <TextField
            label="Enter 6-digit code"
            variant="outlined"
            fullWidth
            value={token}
            onChange={(e) => setToken(e.target.value)}
            sx={{ margin: "12px 0" }}
            inputProps={{ maxLength: 6 }}
          />
          <Button
            variant="contained"
            onClick={handleVerifyMfa}
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : "Verify & Enable"}
          </Button>
        </div>
      ) : (
        <Button
          variant="contained"
          onClick={handleEnableMfa}
          disabled={loading}
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : "Enable MFA"}
        </Button>
      )}

      {backupCodes.length ? (
        <Alert severity="warning" sx={{ mt: 2, alignItems: "flex-start" }}>
          <Stack spacing={1.5} sx={{ width: "100%" }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              Save these backup codes now.
            </Typography>
            <Typography variant="body2">
              Each code can be used once if you lose access to your authenticator app.
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
                gap: 1,
              }}
            >
              {backupCodes.map((code) => (
                <Box
                  key={code}
                  sx={{
                    borderRadius: 1,
                    px: 1.5,
                    py: 1,
                    backgroundColor: "rgba(12, 17, 44, 0.65)",
                    border: "1px solid rgba(255, 255, 255, 0.12)",
                    fontFamily: "monospace",
                    fontSize: 14,
                    letterSpacing: "0.08em",
                  }}
                >
                  {code}
                </Box>
              ))}
            </Box>
            <Box>
              {/* color="inherit" so the button picks up the warning Alert's own (light) text
                  colour. The default outlined button uses the theme's primary blue, which on
                  this dark alert was barely legible. */}
              <Button
                variant="outlined"
                color="inherit"
                onClick={copyBackupCodes}
                disabled={loading}
                sx={{
                  borderColor: "currentColor",
                  fontWeight: 600,
                  "&:hover": {
                    borderColor: "currentColor",
                    backgroundColor: "rgba(255, 255, 255, 0.08)",
                  },
                }}
              >
                Copy Backup Codes
              </Button>
            </Box>
          </Stack>
        </Alert>
      ) : null}

      {status && <div className="mfa-status-msg">{status}</div>}
    </div>
  );
};

export default MfaSetupForm;
